/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Bulk-confirm and pagination handlers for ViewAppointments.
 * @module pages/viewAppointments/useViewAppointmentsConfirmHandlers
 */

import { useCallback } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import { confirmApprovedAndRejectedAppointments } from "../../logic/appointments/confirmApprovedAppointments.js";
import { applyConfirmedAppointmentsInCollections } from "../../logic/appointments/appointmentStateUpdates.js";

/**
 * @param {Object} ctx - ViewAppointments context
 * @param {Object} fetching - Fetch helpers
 * @returns {Object} Confirm modal and pagination handlers
 */
const useViewAppointmentsConfirmHandlers = (ctx, fetching) => {
  const {
    navigate,
    eventData,
    appointments,
    currentPage,
    totalAppointments,
    resultsLimit,
    setShowConfirmModal,
    setIsLoading,
    setAppointments,
    setFilteredRequests,
    setResultsLimit,
    setCurrentPage,
  } = ctx;

  const { fetchAppointmentsPage, fetchCalendarAppointments } = fetching;

  const handleConfirm = useCallback(() => {
    setShowConfirmModal(true);
  }, [setShowConfirmModal]);

  const handleConfirmModalCancel = useCallback(() => {
    setShowConfirmModal(false);
  }, [setShowConfirmModal]);

  const handleConfirmModalConfirm = useCallback(async () => {
    try {
      setIsLoading(true);

      const confirmedAppointmentIds =
        await confirmApprovedAndRejectedAppointments({
          navigate,
          eventId: eventData._id,
          appointments,
        });

      if (confirmedAppointmentIds.size === 0) {
        logger.info("No appointments to confirm");
        setShowConfirmModal(false);
        return;
      }

      applyConfirmedAppointmentsInCollections(confirmedAppointmentIds, {
        setAppointments,
        setFilteredRequests,
      });

      setShowConfirmModal(false);
      await Promise.all([
        fetchAppointmentsPage(currentPage),
        fetchCalendarAppointments(),
      ]);
    } catch (error) {
      logger.error("Error confirming appointments:", {
        err: sanitizeError(error),
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    setIsLoading,
    navigate,
    eventData,
    appointments,
    setShowConfirmModal,
    setAppointments,
    setFilteredRequests,
    fetchAppointmentsPage,
    currentPage,
    fetchCalendarAppointments,
  ]);

  const handleAppointmentsPageChange = useCallback(
    (direction) => {
      const totalPages = Math.ceil(totalAppointments / resultsLimit) || 1;
      if (direction === "next" && currentPage < totalPages) {
        fetchAppointmentsPage(currentPage + 1);
      } else if (direction === "prev" && currentPage > 1) {
        fetchAppointmentsPage(currentPage - 1);
      } else {
        // Unsupported direction or already at bounds. No action required.
      }
    },
    [totalAppointments, resultsLimit, currentPage, fetchAppointmentsPage],
  );

  const handleAppointmentsResultsLimitChange = useCallback(
    (selectedOption) => {
      setResultsLimit(selectedOption.value);
      setCurrentPage(1);
    },
    [setResultsLimit, setCurrentPage],
  );

  return {
    handleConfirm,
    handleConfirmModalConfirm,
    handleConfirmModalCancel,
    handleAppointmentsPageChange,
    handleAppointmentsResultsLimitChange,
  };
};

export default useViewAppointmentsConfirmHandlers;
